import { apiClient } from "@/lib/api-client";
import type { MemoryResp, MemoryUpdateReq } from "@/types/memory.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createMemory } from "./api.ts";

// Shared React Query hooks for memories, used by both web and native via the
// `@/shared` symlink. The only per-app dependency is `@/lib/api-client`
// (resolved against each app's own src/); everything else — React Query, the
// endpoint, the polling rule — is identical across runtimes. UI side effects
// (toasts, navigation) stay in the calling component.

/** Query key for a user's memories, or a specific contact's memories. */
export const memoriesQueryKey = (contactId?: string) =>
  contactId ? (["memories", contactId] as const) : (["memories"] as const);

export const useMemories = (contactId?: string) =>
  useQuery({
    queryKey: memoriesQueryKey(contactId),
    queryFn: async ({ signal }) => {
      const path = contactId ? `/api/a/contacts/${contactId}/memories` : "/api/a/memories";
      const { data } = await apiClient.get<MemoryResp[]>(path, { signal });
      return data;
    },
    // While any memory is still being extracted, poll every 3s so the resolved
    // result replaces its placeholder without a manual refresh. Stop once
    // nothing is processing.
    refetchInterval: (query) =>
      query.state.data?.some((memory) => memory.processing) ? 3000 : false,
  });

/**
 * Create a memory from free text. Invalidates the memories list on success; the
 * new memory comes back still `processing`, and the list query's poll swaps in
 * the extracted version. The caller handles UI side effects (toast, closing the
 * editor).
 */
export const useCreateMemory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMemory,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: memoriesQueryKey() }),
  });
};

/**
 * Update a memory's fields. Invalidates the memories list on success; the
 * caller handles UI side effects (toast, closing the editor) via mutate's
 * onSuccess/onError so this stays runtime-agnostic.
 */
export const useUpdateMemory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MemoryUpdateReq }) =>
      apiClient.post(`/api/a/memories/${id}`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: memoriesQueryKey() }),
  });
};

/**
 * Permanently delete a memory. Invalidates the memories list on success; the
 * caller handles UI side effects (toast, closing the editor, confirmation).
 */
export const useDeleteMemory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.post(`/api/a/memories/${id}/delete`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: memoriesQueryKey() }),
  });
};
